import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { api, type Message } from "../api";
import { inChat, loadOlder, loadRoom, send, setRead, useStore } from "../store";
import { dayLabel, localToday } from "../text";
import { Composer, type ComposerHandle } from "./Composer";
import { MakeTask } from "./MakeTask";
import { continues, MessageItem, type Item } from "./MessageItem";
import { Avatar, isOwner, roomTitle, toast, useMe } from "./ui";
import { editLast, NotifyMenu, PinsButton, ScheduledBar, Typing } from "./ChatBits";
import { Icon } from "./icons";

export function ChatView({ roomId, focus, onMembers }: { roomId: number; focus?: number; onMembers: () => void }) {
  const me = useMe();
  const room = useStore((s) => s.rooms[roomId]);
  const users = useStore((s) => s.users);
  const msgs = useStore((s) => s.msgs[roomId]);
  const older = useStore((s) => s.older[roomId]);
  const pending = useStore((s) => s.pending);
  const st = useStore((s) => s.states[roomId]);
  const list = useRef<HTMLDivElement>(null);
  const comp = useRef<ComposerHandle>(null);
  const atBottom = useRef(true);
  const [making, setMaking] = useState<{ m: Message; el: HTMLElement } | null>(null);
  const [drag, setDrag] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    atBottom.current = true;
    setError(null);
    loadRoom(roomId).catch((e) => setError(e.message || "Couldn't load this chat"));
  }, [roomId]);

  const items = useMemo<Item[]>(() => [
    ...(msgs ?? []).filter(inChat).map((m) => ({ kind: "msg" as const, m })),
    ...pending.filter((p) => p.roomId === roomId && (!p.parentId || p.alsoInChat)).map((p) => ({ kind: "pending" as const, p })),
  ], [msgs, pending, roomId]);

  // Stay pinned to the newest message unless the person scrolled up to read.
  useLayoutEffect(() => {
    const el = list.current;
    if (!el) return;
    if (focus) { document.getElementById(`m-${focus}`)?.scrollIntoView({ block: "center" }); return; }
    if (atBottom.current) el.scrollTop = el.scrollHeight;
  }, [items, focus]);

  // Mark as read while the chat is in view.
  const lastId = msgs?.length ? msgs[msgs.length - 1].id : 0;
  const hold = useStore((s) => !!s.holdUnread[roomId]);
  // Leaving a chat you marked unread lets it be read again next time.
  useEffect(() => () => useStore.setState((s) => { const h = { ...s.holdUnread }; delete h[roomId]; return { holdUnread: h }; }), [roomId]);
  const markRead = useCallback(() => {
    if (hold || !lastId || !atBottom.current || document.visibilityState !== "visible" || !document.hasFocus()) return;
    if (st && st.lastReadId >= lastId) return;
    setRead(roomId, lastId);
    api.read(roomId, lastId).catch(() => {});
  }, [roomId, lastId, st, hold]);

  // A link to an older message: load earlier pages until it's there.
  useEffect(() => {
    if (focus && msgs && older && !msgs.some((m) => m.id === focus) && msgs[0]?.id > focus) loadOlder(roomId).catch(() => {});
  }, [focus, msgs, older, roomId]);
  useEffect(() => {
    markRead();
    addEventListener("focus", markRead);
    document.addEventListener("visibilitychange", markRead);
    return () => { removeEventListener("focus", markRead); document.removeEventListener("visibilitychange", markRead); };
  }, [markRead]);

  const onScroll = () => {
    const el = list.current!;
    atBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
    if (atBottom.current) markRead();
    if (el.scrollTop < 200 && older) {
      const h = el.scrollHeight;
      loadOlder(roomId).then(() => requestAnimationFrame(() => { el.scrollTop += el.scrollHeight - h; }));
    }
  };

  const title = roomTitle(room, me.id, users);
  const people = room?.kind === "group" ? room.members.map((u) => users[u]?.name).filter(Boolean).join(", ") : "";
  let lastDay = "";

  return (
    <div className="col"
      onDragOver={(e) => { if (e.dataTransfer.types.includes("Files")) { e.preventDefault(); setDrag(true); } }}
      onDragLeave={(e) => { if (e.currentTarget === e.target) setDrag(false); }}
      onDrop={(e) => { e.preventDefault(); setDrag(false); comp.current?.addFiles([...e.dataTransfer.files]); }}>
      <div className="head">
        <b>{title}</b>{people && <span>· {people}</span>}
        {room?.kind === "dm" && (() => { const o = users[room.members.find((u) => u !== me.id) ?? me.id]; return o?.status ? <span>· {o.status}</span> : null; })()}
        <span className="r">
          <PinsButton roomId={roomId} />
          <NotifyMenu roomId={roomId} />
          {room?.kind === "group" && <span className="faces">{room.members.filter((u) => u !== me.id).slice(0, 5).map((u) => <Avatar key={u} user={users[u]} size={22} />)}</span>}
          {room?.kind === "group" && isOwner(me) && <button className="hb" onClick={onMembers}><Icon n="people" s={14} />Members</button>}
        </span>
      </div>
      <div className="thread" ref={list} onScroll={onScroll}>
        <div className="spacer" />
        {older && <div className="older">Loading earlier messages…</div>}
        {error && <div className="empty">{error}</div>}
        {msgs && !items.length && <div className="empty">{room?.kind === "dm" ? `This is the start of your chat with ${title}.` : `This is the start of ${title}.`}</div>}
        {items.map((it, i) => {
          const at = it.kind === "pending" ? it.p.createdAt : it.m.createdAt;
          const day = localToday(new Date(at));
          const sep = day !== lastDay ? dayLabel(at) : null;
          lastDay = day;
          return (
            <Fragment key={it.kind === "pending" ? it.p.clientId : it.m.id}>
              {sep && <div className="day">{sep}</div>}
              <MessageItem item={it} cont={!sep && continues(items[i - 1], it, me.id)} focus={it.kind === "msg" && it.m.id === focus}
                onMakeTask={(m, el) => setMaking({ m, el })} />
            </Fragment>
          );
        })}
      </div>
      <ScheduledBar roomId={roomId} parentId={null} />
      <Typing roomId={roomId} parentId={null} />
      <Composer ref={comp} draftKey={`room-${roomId}`} placeholder={`Message ${title}`} autoFocus roomId={roomId}
        onEditLast={() => editLast(msgs ?? [], me.id)}
        onSchedule={async (body, files, _also, at) => { try { useStore.setState({ scheduled: await api.schedule({ roomId, body, sendAt: at.toISOString(), fileIds: files.map((f) => f.id) }) }); } catch (e: any) { toast(e.message || "Couldn't schedule"); return false; } }}
        onSend={(body, files) => { atBottom.current = true; useStore.setState((s) => { const h = { ...s.holdUnread }; delete h[roomId]; return { holdUnread: h }; }); send({ roomId, body, files }).catch(() => toast("Not sent")); }} />
      {making && <MakeTask m={making.m} anchor={making.el} onClose={() => setMaking(null)} />}
      {drag && <div className="drop">Drop files to send them to {title}</div>}
    </div>
  );
}
